import { AgGridModule } from '@ag-grid-community/angular';
import {
  GridApi,
  GridOptions,
  IRowNode,
  SelectionChangedEvent,
} from '@ag-grid-community/core';
import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormlyFieldInput } from '@ngx-formly/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CollectiveSourcingPurchaserResponse,
  CompanyXatSvc,
} from 'net-api-lib';
import { combineLatest, map, startWith } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-formly-purchasers-select',
  imports: [AgGridModule, TranslateModule],
  templateUrl: './formly-purchasers-select.component.html',
  styleUrl: './formly-purchasers-select.component.scss',
})
export class FormlyPurchasersSelectComponent
  extends FormlyFieldInput
  implements OnInit, OnDestroy
{
  translateSvc = inject(TranslateService);
  companyXatSvc = inject(CompanyXatSvc);

  purchasersSignal: WritableSignal<
    CollectiveSourcingPurchaserResponse[] | undefined
  > = signal(undefined);

  private subs = new SubSink();
  private gridApi?: GridApi<CollectiveSourcingPurchaserResponse>;

  private get selectedPurchaserIds(): number[] | undefined {
    return this.formControl.value;
  }

  // observables
  private purchasers$ = this.companyXatSvc
    .getCollectiveSourcingPurchasers()
    .pipe(map(r => (r.successes ? r.content! : [])));

  gridOptions: GridOptions = {
    rowSelection: {
      mode: 'multiRow',
      enableSelectionWithoutKeys: true,
      enableClickSelection: true,
      headerCheckbox: false,
    },
    headerHeight: 0,
    columnDefs: [
      {
        field: 'name',
        flex: 1,
      },
    ],
    onSelectionChanged: (
      event: SelectionChangedEvent<CollectiveSourcingPurchaserResponse>
    ) => {
      const selectedRows = event.api.getSelectedRows();
      this.formControl.setValue(selectedRows.map(r => r.oid));
    },
    onGridReady: params => {
      this.gridApi = params.api;
    },
    onFirstDataRendered: () =>
      this.selectedPurchaserIds
        ? this.selectPurchasersInGrid(this.selectedPurchaserIds!)
        : null,
  };

  ngOnInit() {
    // load purchasers initially, sort them selected/not selected
    // also select purchasers in grid or disable the row-selection
    this.subs.sink = combineLatest([
      this.purchasers$,
      this.formControl.valueChanges.pipe(startWith(this.formControl.value)),
    ]).subscribe(([purchasers, selectedPurchaserIds]) => {
      const purchasersSorted = purchasers.sort((a, b) => {
        if (selectedPurchaserIds && selectedPurchaserIds.length > 0) {
          if (selectedPurchaserIds.includes(a.oid!)) return -1;
          if (selectedPurchaserIds.includes(b.oid!)) return 1;
        }
        return 0;
      });

      this.purchasersSignal.set(purchasersSorted);

      if (this.formControl.enabled)
        this.selectPurchasersInGrid(selectedPurchaserIds);
      else
        setTimeout(() => {
          this.gridApi?.forEachNode(rowNode => {
            rowNode.selectable = false;
          });
        });
    });

    // disable grid when form control is disabled
    this.formControl.registerOnDisabledChange(isDisabled => {
      this.gridApi?.forEachNode(rowNode => (rowNode.selectable = !isDisabled));

      if (isDisabled) this.gridApi?.deselectAll();
    });
  }

  private selectPurchasersInGrid(value: number[]) {
    if (!this.gridApi) return;

    const nodesToSelect: IRowNode[] = [];
    this.gridApi.forEachNode(
      (node: IRowNode<CollectiveSourcingPurchaserResponse>) => {
        if (node.data?.oid && value.includes(node.data.oid))
          nodesToSelect.push(node);
      }
    );

    this.gridApi.setNodesSelected({
      nodes: nodesToSelect,
      newValue: true,
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
